<template>
  <CModal
    :show="show"
    size="lg"
    :centered="true"
    :title="modalTitle"
    class="mvp-mark-modal"
    @update:show="onShowChanged"
  >
    <div v-if="order" class="mvp-mark-body">
      <div class="mvp-mark-section">
        <div class="mvp-mark-row">
          <span class="mvp-mark-key">Sl No</span>
          <span class="mvp-mark-val">{{ slNo || '—' }}</span>
        </div>
        <div class="mvp-mark-row">
          <span class="mvp-mark-key">Order Number</span>
          <span class="mvp-mark-val mvp-mark-val--mono">{{ order.order_number || '—' }}</span>
        </div>
        <div class="mvp-mark-row">
          <span class="mvp-mark-key">Buyer</span>
          <span class="mvp-mark-val">{{ order.contact_name || '—' }}</span>
        </div>
        <div class="mvp-mark-row">
          <span class="mvp-mark-key">Products</span>
          <span class="mvp-mark-val mvp-mark-val--wrap">{{ order.products || '—' }}</span>
        </div>
        <div class="mvp-mark-row">
          <span class="mvp-mark-key">Amount</span>
          <span class="mvp-mark-val">{{ formatAmount(order.grand_total) }}</span>
        </div>
        <div class="mvp-mark-row">
          <span class="mvp-mark-key">Date</span>
          <span class="mvp-mark-val">{{ formatDate(order.created_at) }}</span>
        </div>
      </div>

      <div class="mvp-mark-care-of">
        <label class="mvp-mark-care-of__label">{{ careOfLabel }}</label>
        <AppDropdown
          :value="careOfValue"
          :options="careOfOptions"
          searchable
          search-placeholder="Search agents…"
          placeholder="Unknown"
          @input="careOfValue = $event"
        />
      </div>
    </div>

    <template #footer>
      <CButton color="secondary" variant="outline" :disabled="submitting" class="mvp-cancel-btn" @click="close">
        Cancel
      </CButton>
      <CButton color="primary" :disabled="submitting || !order" class="mvp-submit-btn" @click="submit">
        {{ submitting ? 'Saving…' : 'Submit' }}
      </CButton>
    </template>
  </CModal>
</template>

<script>
import AppDropdown from '@/components/AppDropdown.vue'

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export default {
  name: 'MarkOrderModal',
  components: { AppDropdown },
  props: {
    show: { type: Boolean, default: false },
    order: { type: Object, default: null },
    users: { type: Array, default: () => [] },
    mode: { type: String, default: 'mark' },
    slNo: { type: [Number, String], default: '' },
    submitting: { type: Boolean, default: false }
  },
  data () {
    return {
      careOfValue: 0
    }
  },
  computed: {
    modalTitle () {
      return this.mode === 'change' ? 'Change Care Of' : 'Mark Care Of'
    },
    careOfLabel () {
      return this.mode === 'change' ? 'Change Order Care Of' : 'Choose Order Care Of'
    },
    careOfOptions () {
      return [
        { value: 0, label: 'Unknown' },
        ...this.users.map(u => ({ value: Number(u.id), label: u.name }))
      ]
    }
  },
  watch: {
    show (val) {
      if (val) {
        this.careOfValue = this.initialCareOf()
      }
    },
    order () {
      this.careOfValue = this.initialCareOf()
    }
  },
  methods: {
    initialCareOf () {
      if (this.order && this.order.care_of != null) {
        return Number(this.order.care_of) || 0
      }
      return 0
    },
    close () {
      this.$emit('close')
    },
    onShowChanged (next) {
      if (!next) this.close()
    },
    submit () {
      if (!this.order) return
      this.$emit('submit', {
        orderNumber: this.order.id,
        careOf: Number(this.careOfValue) || 0
      })
    },
    formatAmount (value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '—'
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2
      }).format(num)
    },
    formatDate (value) {
      if (!value) return '—'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return value
      const day = String(d.getDate()).padStart(2, '0')
      const month = MONTHS_SHORT[d.getMonth()]
      return `${day} ${month} ${d.getFullYear()}`
    }
  }
}
</script>

<style scoped>
.mvp-mark-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.mvp-mark-modal >>> .modal-header {
  border-bottom: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.95rem 1.15rem;
}

.mvp-mark-modal >>> .modal-header .modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  letter-spacing: -0.01em;
}

.mvp-mark-modal >>> .modal-body {
  padding: 1.1rem;
  background: #fbfcfe;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.mvp-mark-modal >>> .modal-footer {
  border-top: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.7rem 1.15rem;
  gap: 0.5rem;
}

.mvp-mark-modal >>> .modal-footer .btn {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.5rem 1.15rem;
  margin: 0;
  min-width: 100px;
}

.mvp-mark-body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.mvp-mark-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow: hidden;
}

.mvp-mark-row {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 0.75rem;
  padding: 0.7rem 0.95rem;
  border-bottom: 1px solid #eef1f7;
  align-items: start;
}

.mvp-mark-row:last-child {
  border-bottom: 0;
}

.mvp-mark-section .mvp-mark-row:nth-child(even) {
  background: #fafbfd;
}

.mvp-mark-key {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.mvp-mark-val {
  font-size: 0.82rem;
  color: #374151;
  word-break: break-word;
}

.mvp-mark-val--wrap {
  line-height: 1.45;
}

.mvp-mark-val--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-weight: 600;
}

.mvp-mark-care-of {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 0.85rem 0.95rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.mvp-mark-care-of__label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #475569;
  margin: 0;
}

.mvp-submit-btn {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: none;
  color: #ffffff;
  transition: transform 0.15s ease, filter 0.15s ease;
}

.mvp-submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.05);
}

.mvp-cancel-btn {
  border: 1px solid #d8dde6;
  color: #475569;
  background: #ffffff;
}
</style>
